import { useState, useEffect } from "react";
import Footer from "./Footer";
import config from "../config";

const { API_BASE, UPLOADS_URL } = config;

async function fetchJSON(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status} – ${url}`);
  return res.json();
}

function parseCoreValues(raw = "") {
  if (!raw) return [];
  return raw
    .split(/\r?\n/)
    .map((line) => {
      const trimmed = line.trim();
      if (!trimmed) return null;
      const colonIdx = trimmed.indexOf(":");
      if (colonIdx === -1) return null;
      const title = trimmed.slice(0, colonIdx).trim();
      const desc  = trimmed.slice(colonIdx + 1).trim();
      return title && desc ? { title, desc } : null;
    })
    .filter(Boolean);
}

const ICON_FILES = [
  "cooler.svg",
  "emergency-repair.svg",
  "heating.svg",
  "residential.svg",
];

const ACCORDION_TABS = [
  { key: "mission_statement", label: "Mission Statement", imageKey: "image3" },
  { key: "vision",            label: "Vision",            imageKey: "image4" },
  { key: "objectives",        label: "Our Objectives",    imageKey: "image5" },
  { key: "core_values",       label: "Core Values",       imageKey: "image4" },
];

/* "- " / "• " bullet lines → plain string array (used for objectives) */
function parseBulletLines(raw = "") {
  if (!raw) return [];
  return raw
    .split(/\r?\n/)
    .map((line) => line.replace(/^\s*[-•]\s*/, "").trim())
    .filter(Boolean);
}

/* Plain free-text → one <p> per non-empty line (used for mission/vision) */
function parseParagraphLines(raw = "") {
  if (!raw) return [];
  return raw.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
}

const AccordionTickIcon = () => (
  <svg width="11" height="11" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M3.5 8.2l2.8 2.8 6.2-6.2" stroke="#fff" strokeWidth="2.4"
      strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/* Renders the right-hand panel content for whichever tab is active.
   - objectives: "- " bullet lines → checklist, each its own line.
   - core_values: "Label: description" lines → checklist, label bolded.
   - mission_statement / vision: plain paragraph(s). */
function AccordionPanelContent({ tabKey, raw }) {
  if (tabKey === "objectives") {
    const items = parseBulletLines(raw);
    if (!items.length) return <p className="ab-acc__empty">No objectives listed yet.</p>;
    return (
      <ul className="ab-acc__checklist">
        {items.map((item, i) => (
          <li key={i} className="ab-acc__check-item">
            <span className="ab-acc__check-icon" aria-hidden="true"><AccordionTickIcon /></span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    );
  }

  if (tabKey === "core_values") {
    const values = parseCoreValues(raw);
    if (!values.length) return <p className="ab-acc__empty">No core values listed yet.</p>;
    return (
      <ul className="ab-acc__checklist">
        {values.map((v, i) => (
          <li key={i} className="ab-acc__check-item">
            <span className="ab-acc__check-icon" aria-hidden="true"><AccordionTickIcon /></span>
            <span><strong>{v.title}:</strong> {v.desc}</span>
          </li>
        ))}
      </ul>
    );
  }

  // mission_statement / vision
  const paragraphs = parseParagraphLines(raw);
  if (!paragraphs.length) return <p className="ab-acc__empty">Content coming soon.</p>;
  return (
    <>
      {paragraphs.map((p, i) => (
        <p key={i} className="ab-acc__text">{p}</p>
      ))}
    </>
  );
}

function AboutAccordion({ about }) {
  const [active, setActive] = useState(0);

  const tabs = ACCORDION_TABS
    .map((t) => ({
      ...t,
      raw: about?.[t.key] || "",
      image: about?.[t.imageKey] || null,
    }))
    .filter((t) => t.raw);

  if (!tabs.length) return null;

  const safeActive = Math.min(active, tabs.length - 1);
  const currentTab = tabs[safeActive];

  return (
    <div className="ab-accordion">
      <div className="ab-accordion__inner">

        <div className="ab-accordion__tabs" role="tablist">
          {tabs.map((t, i) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={safeActive === i}
              className={`ab-accordion__tab ${safeActive === i ? "ab-accordion__tab--active" : ""}`}
              onClick={() => setActive(i)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="ab-accordion__panel">
          <div className="ab-accordion__media">
            {currentTab.image ? (
              <img
                src={`${UPLOADS_URL}/about/${currentTab.image}`}
                alt={currentTab.label}
                className="ab-accordion__img"
              />
            ) : (
              <div className="ab-accordion__img ab-accordion__img--placeholder" />
            )}
          </div>
          <div className="ab-accordion__content">
            <h3 className="ab-accordion__heading">{currentTab.label}</h3>
            <AccordionPanelContent tabKey={currentTab.key} raw={currentTab.raw} />
          </div>
        </div>

      </div>
    </div>
  );
}

function Breadcrumb() {
  return (
    <nav className="ab-bread" aria-label="Breadcrumb">
      <a href="/" className="ab-bread__link">
        <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" style={{ marginRight: 4 }}>
          <path d="M8.354 1.146a.5.5 0 0 0-.708 0l-6 6A.5.5 0 0 0 1.5 8v6a.5.5 0 0 0 .5.5h3.5a.5.5 0 0 0 .5-.5v-3h4v3a.5.5 0 0 0 .5.5H14a.5.5 0 0 0 .5-.5V8a.5.5 0 0 0-.146-.354L8.354 1.146z"/>
        </svg>
        Home
      </a>
      <span className="ab-bread__sep" aria-hidden="true">|</span>
      <span className="ab-bread__current">About</span>
    </nav>
  );
}

/* ── Image block: two overlapping photos from about_us.image1 / image2 ── */
function AboutImages({ image1, image2 }) {
  return (
    <div className="ab-img-stack">
      {image1 ? (
        <img
          src={`${UPLOADS_URL}/about/${image1}`}
          alt="Our team at work"
          className="ab-img-stack__back"
        />
      ) : (
        <div className="ab-img-stack__back ab-img-stack__placeholder" />
      )}

      {image2 && (
        <div className="ab-img-stack__front-wrap">
          <img
            src={`${UPLOADS_URL}/about/${image2}`}
            alt="Our expert technicians"
            className="ab-img-stack__front"
          />
        </div>
      )}

      <div className="ab-img-stack__badge" aria-label="10+ years of experience">
        <span className="ab-img-stack__badge-years">10+</span>
        <span className="ab-img-stack__badge-line1">YEARS OF</span>
        <span className="ab-img-stack__badge-line2">EXPERIENCE</span>
      </div>
    </div>
  );
}

function CoreValueCard({ title, desc, icon }) {
  return (
    <div className="ab-card">
      <div className="ab-card__icon">
        <img
          src={`${UPLOADS_URL}/svg-icons/${icon}`}
          alt=""
          aria-hidden="true"
          onError={(e) => { e.currentTarget.style.opacity = "0"; }}
        />
      </div>
      <div className="ab-card__body">
        <h3 className="ab-card__title">{title}</h3>
        <p className="ab-card__desc">{desc}</p>
      </div>
    </div>
  );
}

/* ── FAQ accordion section: left intro + CTA card, right accordion list ── */
function FaqSection({ faqs, companyName }) {
  const [open, setOpen] = useState(0);

  if (!faqs?.length) return null;

  return (
    <div className="ab-faq">
      <div className="ab-faq__inner">

        {/* LEFT: intro + still-have-questions card */}
        <div className="ab-faq__intro">
          <div className="ab-content__eyebrow">
            <span className="ab-content__eyebrow-icon" aria-hidden="true">✦</span>
            FAQs
          </div>
          <h2 className="ab-faq__heading">Frequently Asked Questions</h2>
          <p className="ab-faq__desc">
            Have questions about our HVAC services? Find quick answers to common queries about installation, maintenance, repairs, and pricing. Our goal is to provide clear information and help you make confident decisions.
          </p>

          <div className="ab-faq__cta">
            <h3 className="ab-faq__cta-heading">Still Have Questions?</h3>
            <p className="ab-faq__cta-desc">
              Our team is here to help. Contact {companyName} today for expert advice and reliable HVAC solutions.
            </p>
            <a href="/contact-us" className="ab-faq__cta-btn">
              Contact Us
              <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.8"
                  strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </a>
          </div>
        </div>

        {/* RIGHT: accordion list */}
        <div className="ab-faq__list">
          {faqs.map((faq, i) => (
            <div key={faq.id ?? i} className={`ab-faq__item ${open === i ? "ab-faq__item--open" : ""}`}>
              <button
                className="ab-faq__q"
                onClick={() => setOpen(open === i ? null : i)}
                aria-expanded={open === i}
              >
                {faq.title}
                <span className="ab-faq__icon" aria-hidden="true">{open === i ? "−" : "+"}</span>
              </button>
              {open === i && (
                <div className="ab-faq__a">
                  {faq.image_filename && (
                    <img
                      src={`${UPLOADS_URL}/faq/${faq.image_filename}`}
                      alt=""
                      aria-hidden="true"
                      className="ab-faq__a-img"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  )}
                  <p className="ab-faq__a-text">{faq.description}</p>
                </div>
              )}
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}

function AboutPage() {
  const [about,   setAbout]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    fetchJSON(`${API_BASE}/about.php`)
      .then((data) => {
        if (cancelled) return;
        setAbout(data || null);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("AboutPage load error:", err);
        setError("Failed to load page.");
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  if (loading) return null;

  if (error) return (
    <div className="ab-error">
      <h2>Something went wrong</h2>
      <p>We could not load the about page. Please try again later.</p>
      <a href="/" className="ab-error__link">Back to Home</a>
    </div>
  );

  const coreValues   = parseCoreValues(about?.core_values);
  const content      = about?.content || "";
  const pageTitle    = about?.title || "About Us";
  const companyName  = about?.company_name || "Us";
  const image1       = about?.image1 || null;
  const image2       = about?.image2 || null;
  const image3       = about?.image3 || null;
  const heroImage    = about?.hero_image || null;
  const phoneNumber  = about?.phone_number || null;
  const faqs         = Array.isArray(about?.faqs) ? about.faqs : [];

  return (
    <>
      <div className="ab">

        {/* ── HERO ── */}
        <div className="ab-hero">
          {heroImage && (
            <img
              src={`${UPLOADS_URL}/about/${heroImage}`}
              alt={pageTitle}
              className="ab-hero__img"
            />
          )}
          <div className="ab-hero__overlay" />
          <div className="ab-hero__content">
            <Breadcrumb />
            <h1 className="ab-hero__title">{pageTitle}</h1>
          </div>
        </div>

        {/* ── BODY ── */}
        <div className="ab-body">
          <div className="ab-body__inner">

            {/* LEFT: about_us.image1 (back) + about_us.image2 (front, white border) */}
            <AboutImages image1={image1} image2={image2} />

            {/* RIGHT: content */}
            <div className="ab-content">

              <div className="ab-content__eyebrow">
                <span className="ab-content__eyebrow-icon" aria-hidden="true">✦</span>
                About {companyName}
              </div>

              <h2 className="ab-content__heading">
                Make the weather behave!
              </h2>

              {/* about_us.content */}
              <p className="ab-content__text">
                {content || "We specialize in delivering reliable and energy-efficient HVAC solutions for modern homes and businesses. With years of hands-on experience, our certified technicians provide everything from fast AC repairs to complete heating and cooling system installations."}
              </p>

              {/* about_us.core_values parsed into cards */}
              {coreValues.length > 0 && (
                <div className="ab-cards">
                  {coreValues.slice(0, 4).map((val, i) => (
                    <CoreValueCard
                      key={i}
                      title={val.title}
                      desc={val.desc}
                      icon={ICON_FILES[i % ICON_FILES.length]}
                    />
                  ))}
                </div>
              )}

              <ul className="ab-checks">
                <li className="ab-checks__item">
                  <span className="ab-checks__icon" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425a.247.247 0 0 1 .02-.022Z"/>
                    </svg>
                  </span>
                  Certified &amp; Experienced HVAC Technicians
                </li>
                <li className="ab-checks__item">
                  <span className="ab-checks__icon" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425a.247.247 0 0 1 .02-.022Z"/>
                    </svg>
                  </span>
                  Energy-Efficient &amp; Cost-Saving Solutions
                </li>
              </ul>

              {/* company_contact.phone_number via JOIN */}
              <div className="ab-cta">
                <div className="ab-cta__phone">
                  <span className="ab-cta__phone-icon" aria-hidden="true">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M3.654 1.328a.678.678 0 0 0-1.015-.063L1.605 2.3c-.483.484-.661 1.169-.45 1.77a17.568 17.568 0 0 0 4.168 6.608 17.569 17.569 0 0 0 6.608 4.168c.601.211 1.286.033 1.77-.45l1.034-1.034a.678.678 0 0 0-.063-1.015l-2.307-1.794a.678.678 0 0 0-.58-.122l-2.19.547a1.745 1.745 0 0 1-1.657-.459L5.482 8.062a1.745 1.745 0 0 1-.46-1.657l.548-2.19a.678.678 0 0 0-.122-.58L3.654 1.328z"/>
                    </svg>
                  </span>
                  <div className="ab-cta__phone-text">
                    <span className="ab-cta__phone-label">Need Any Help</span>
                    <a
                      href={phoneNumber ? `tel:${phoneNumber}` : "#"}
                      className="ab-cta__phone-number"
                    >
                      {phoneNumber || "Contact Us"}
                    </a>
                  </div>
                </div>
                <a href="/contact-us" className="ab-cta__btn">
                  Learn More About Us
                  <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                    <path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.8"
                      strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </a>
              </div>

            </div>
          </div>
        </div>

        {/* ── FIXED-BACKGROUND CTA BAND ──
            Extra bottom padding here makes room for the accordion below
            to overlap the lower third of this image without covering
            the "Need Fast..." card itself (which stays pinned near the
            top of the band via the unchanged top padding). */}
        {image3 && (
          <div
            className="ab-parallax"
            style={{ backgroundImage: `url(${UPLOADS_URL}/about/${image3})` }}
          >
            <div className="ab-parallax__overlay" />
            <div className="ab-parallax__inner">
              <div className="ab-parallax__card">
                <h2 className="ab-parallax__heading">
                  Need Fast And Reliable<br />HVAC Service Today?
                </h2>
                <p className="ab-parallax__desc">
                  Our team is available 24/7 to handle urgent heating and cooling issues quickly and efficiently.
                </p>
                <div className="ab-parallax__row">
                  <div className="ab-parallax__phone">
                    <span className="ab-parallax__phone-icon" aria-hidden="true">
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M3.654 1.328a.678.678 0 0 0-1.015-.063L1.605 2.3c-.483.484-.661 1.169-.45 1.77a17.568 17.568 0 0 0 4.168 6.608 17.569 17.569 0 0 0 6.608 4.168c.601.211 1.286.033 1.77-.45l1.034-1.034a.678.678 0 0 0-.063-1.015l-2.307-1.794a.678.678 0 0 0-.58-.122l-2.19.547a1.745 1.745 0 0 1-1.657-.459L5.482 8.062a1.745 1.745 0 0 1-.46-1.657l.548-2.19a.678.678 0 0 0-.122-.58L3.654 1.328z"/>
                      </svg>
                    </span>
                    <div className="ab-parallax__phone-text">
                      <span className="ab-parallax__phone-label">Need Any Help</span>
                      <a
                        href={phoneNumber ? `tel:${phoneNumber}` : "#"}
                        className="ab-parallax__phone-number"
                      >
                        {phoneNumber || "Contact Us"}
                      </a>
                    </div>
                  </div>
                  <a href="/contact-us" className="ab-parallax__btn">
                    Contact Us
                    <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                      <path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.8"
                        strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        <AboutAccordion about={about} />

        {/* ── FAQ ── */}
        <FaqSection faqs={faqs} companyName={companyName} />

        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700;800;900&display=swap');

          .ab *, .ab *::before, .ab *::after { box-sizing: border-box; margin: 0; padding: 0; }
          .ab a { text-decoration: none; }
          .ab ul { list-style: none; }
          .ab { font-family: 'Roboto', sans-serif; background: #f5f7fa; }

          .ab-error { text-align: center; padding: 80px 24px; font-family: 'Roboto', sans-serif; color: #424244; }
          .ab-error h2 { font-size: 28px; color: #013d97; margin-bottom: 12px; }
          .ab-error p  { margin-bottom: 24px; }
          .ab-error__link { display: inline-block; padding: 10px 24px; background: #029add; color: #fff; border-radius: 6px; font-weight: 700; }

          .ab-hero { position: relative; height: 260px; overflow: hidden; background: #013d97; }
          .ab-hero__img { width: 100%; height: 100%; object-fit: cover; object-position: center; display: block; }
          .ab-hero__overlay { position: absolute; inset: 0; background: linear-gradient(to right, rgba(1,21,70,.8) 40%, rgba(1,61,151,.45) 100%); }
          .ab-hero__content { position: absolute; inset: 0; display: flex; flex-direction: column; justify-content: flex-end; padding: 32px 40px; max-width: 1280px; margin: 0 auto; left: 0; right: 0; }
          .ab-hero__title { font-size: clamp(28px, 3.5vw, 46px); font-weight: 900; color: #fff; line-height: 1.1; margin-top: 10px; }

          .ab-bread { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
          .ab-bread__link { font-size: 13px; font-weight: 600; color: rgba(255,255,255,.85); display: flex; align-items: center; }
          .ab-bread__link:hover { color: #fff; }
          .ab-bread__sep { color: rgba(255,255,255,.5); font-size: 14px; }
          .ab-bread__current { font-size: 13px; color: rgba(255,255,255,.7); }

          .ab-body { padding: 72px 0 80px; }
          .ab-body__inner { max-width: 1280px; margin: 0 auto; padding: 0 40px; display: grid; grid-template-columns: 1fr 1fr; gap: 64px; align-items: center; }

          .ab-img-stack { position: relative; padding-bottom: 40px; }
          .ab-img-stack__back { display: block; width: 75%; border-radius: 4px; object-fit: cover; aspect-ratio: 3/4; box-shadow: 0 8px 32px rgba(0,0,0,.14); }
          .ab-img-stack__placeholder { width: 75%; aspect-ratio: 3/4; border-radius: 4px; background: linear-gradient(135deg, #013d97, #029add); }
          .ab-img-stack__front-wrap { position: absolute; right: 0; bottom: -40px; width: 58%; border: 6px solid #fff; border-radius: 4px; box-shadow: 0 8px 32px rgba(0,0,0,.18); overflow: hidden; background: #fff; }
          .ab-img-stack__front { display: block; width: 100%; aspect-ratio: 3/4; object-fit: cover; }

          .ab-img-stack__badge { position: absolute; bottom: 0; left: 20px; width: 120px; height: 120px; border-radius: 50%; background: conic-gradient(from 0deg, #013d97 0%, #029add 50%, #013d97 100%); display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 1px; box-shadow: 0 4px 16px rgba(0,0,0,.22); border: 3px solid #fff; }
          .ab-img-stack__badge-years { font-size: 28px; font-weight: 1100; color: #fff; line-height: 1; }
          .ab-img-stack__badge-line1, .ab-img-stack__badge-line2 { font-size: 12px; font-weight: 800; color: rgba(255,255,255,.9); letter-spacing: .08em; text-transform: uppercase; line-height: 1.2; }

          .ab-content { display: flex; flex-direction: column; gap: 22px; }
          .ab-content__eyebrow { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 700; color: #029add; letter-spacing: .08em; text-transform: uppercase; }
          .ab-content__eyebrow-icon { font-size: 14px; color: #029add; }
          .ab-content__heading { font-size: clamp(24px, 2.8vw, 36px); font-weight: 900; color: #1a1a2e; line-height: 1.2; }
          .ab-content__text { font-size: 14.5px; color: #5a5f72; line-height: 1.8; text-align: justify; }

          .ab-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
          .ab-card { display: flex; align-items: flex-start; gap: 14px; padding: 18px; background: #fff; border: 1px solid #eef0f5; border-radius: 6px; box-shadow: 0 2px 10px rgba(0,0,0,.05); transition: border-color .2s, box-shadow .2s; }
          .ab-card:hover { border-color: #029add; box-shadow: 0 4px 18px rgba(2,154,221,.12); }
          .ab-card__icon { width: 44px; height: 44px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; background: #f0f8fd; border-radius: 8px; }
          .ab-card__icon img { width: 26px; height: 26px; object-fit: contain; }
          .ab-card__title { font-size: 13px; font-weight: 800; color: #1a1a2e; margin-bottom: 5px; line-height: 1.3; }
          .ab-card__desc { font-size: 12.5px; color: #6b7280; line-height: 1.55; }

          .ab-checks { display: flex; flex-direction: column; gap: 10px; }
          .ab-checks__item { display: flex; align-items: center; gap: 10px; font-size: 14px; font-weight: 600; color: #424244; }
          .ab-checks__icon { width: 22px; height: 22px; flex-shrink: 0; border-radius: 50%; background: #013d97; color: #fff; display: flex; align-items: center; justify-content: center; }

          .ab-cta { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; margin-top: 6px; }
          .ab-cta__phone { display: flex; align-items: center; gap: 12px; }
          .ab-cta__phone-icon { width: 44px; height: 44px; border-radius: 50%; flex-shrink: 0; background: #013d97; color: #fff; display: flex; align-items: center; justify-content: center; }
          .ab-cta__phone-text { display: flex; flex-direction: column; gap: 2px; }
          .ab-cta__phone-label { font-size: 11.5px; color: #8890a0; font-weight: 600; }
          .ab-cta__phone-number { font-size: 15px; font-weight: 800; color: #1a1a2e; transition: color .2s; }
          .ab-cta__phone-number:hover { color: #029add; }
          .ab-cta__btn { display: inline-flex; align-items: center; gap: 8px; background: #029add; color: #fff; font-size: 13px; font-weight: 800; letter-spacing: .04em; padding: 13px 22px; border-radius: 3px; transition: background .2s, transform .2s; white-space: nowrap; }
          .ab-cta__btn:hover { background: #c85a1f; transform: translateY(-2px); }

          /* ── FIXED-BACKGROUND CTA BAND ──
             Bottom padding extended (110px → 240px) so the card stays
             near the top of the band, leaving open image area at the
             bottom for the accordion below to overlap into. */
          .ab-parallax {
            position: relative;
            background-color: #0c1430;
            background-size: cover;
            background-position: center;
            background-repeat: no-repeat;
            background-attachment: fixed;
            padding: 110px 0 240px;
            overflow: hidden;
            isolation: isolate;
          }
          .ab-parallax__overlay {
            position: absolute; inset: 0;
            background: linear-gradient(to right, rgba(5,10,28,.92) 30%, rgba(5,10,28,.35) 75%, rgba(5,10,28,.1) 100%);
          }
          .ab-parallax__inner {
            position: relative; z-index: 1;
            max-width: 1280px; margin: 0 auto; padding: 0 40px;
          }
          .ab-parallax__card {
            max-width: 560px;
            background: rgba(20,26,48,.45);
            border-radius: 8px;
            padding: 40px;
            backdrop-filter: blur(2px);
          }
          .ab-parallax__heading {
            font-size: clamp(26px, 3.2vw, 38px); font-weight: 900;
            color: #fff; line-height: 1.2; margin-bottom: 18px;
          }
          .ab-parallax__desc {
            font-size: 15px; color: rgba(255,255,255,.78);
            line-height: 1.7; margin-bottom: 28px;
          }
          .ab-parallax__row { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; }
          .ab-parallax__phone { display: flex; align-items: center; gap: 12px; }
          .ab-parallax__phone-icon {
            width: 44px; height: 44px; border-radius: 50%; flex-shrink: 0;
            background: #013d97; color: #fff;
            display: flex; align-items: center; justify-content: center;
          }
          .ab-parallax__phone-text { display: flex; flex-direction: column; gap: 2px; }
          .ab-parallax__phone-label { font-size: 11.5px; color: rgba(255,255,255,.6); font-weight: 600; }
          .ab-parallax__phone-number { font-size: 17px; font-weight: 800; color: #fff; transition: color .2s; }
          .ab-parallax__phone-number:hover { color: #6fc6f2; }
          .ab-parallax__btn {
            display: inline-flex; align-items: center; gap: 8px;
            background: #029add; color: #fff;
            font-size: 13px; font-weight: 800; letter-spacing: .04em;
            padding: 13px 24px; border-radius: 3px;
            transition: background .2s, transform .2s;
            white-space: nowrap;
          }
          .ab-parallax__btn:hover { background: #029add; transform: translateY(-2px); }

          /* ── MISSION / VISION / OBJECTIVES / CORE VALUES ACCORDION ── */
          .ab-accordion {
            position: relative;
            z-index: 2;
            margin-top: -200px;
            padding: 0 0 64px;
          }
          .ab-accordion__inner { max-width: 1280px; margin: 0 auto; padding: 0 40px; }

          .ab-accordion__tabs {
            display: flex;
            background: #161b2e;
            border-radius: 4px 4px 0 0;
            overflow: hidden;
          }
          .ab-accordion__tab {
            flex: 1;
            padding: 22px 18px;
            background: #161b2e;
            color: rgba(255,255,255,.65);
            -webkit-font-smoothing: antialiased;
            font-size: 12.5px;
            font-weight: 800;
            letter-spacing: .04em;
            text-transform: uppercase;
            text-align: center;
            border: none;
            border-right: 1px solid rgba(255,255,255,.08);
            cursor: pointer;
            transition: background .2s, color .2s;
          }
          .ab-accordion__tab:last-child { border-right: none; }
          .ab-accordion__tab:hover { color: #fff; background: #1e2540; }
          .ab-accordion__tab--active {
            background: #029add;
            color: #fff !important;
            box-shadow: inset 0 -3px 0 #c85a1f;
          }

          .ab-accordion__panel {
            display: grid;
            grid-template-columns: 0.85fr 1.15fr;
            background: #0c1022;
            border-radius: 0 0 4px 4px;
            overflow: hidden;
            min-height: 380px;
            box-shadow: 0 20px 50px rgba(0,0,0,.28);
          }

          .ab-accordion__media { position: relative; min-height: 380px; }
          .ab-accordion__img { width: 100%; height: 100%; object-fit: cover; display: block; }
          .ab-accordion__img--placeholder { background: linear-gradient(135deg, #013d97, #029add); }

          .ab-accordion__content {
            padding: 44px;
            display: flex;
            flex-direction: column;
            gap: 22px;
            justify-content: center;
          }
          .ab-accordion__heading { font-size: 24px; font-weight: 900; color: #fff; }

          .ab-acc__text { color: #ffffff; font-size: 14.5px; line-height: 1.8; }
          .ab-acc__checklist { display: flex; flex-direction: column; gap: 16px; }
          .ab-acc__check-item { display: flex; align-items: flex-start; gap: 14px; font-size: 14.5px; color: #ffffff; line-height: 1.6; }
          .ab-acc__check-icon { flex-shrink: 0; width: 22px; height: 22px; margin-top: 1px; background: #029add; border-radius: 4px; display: flex; align-items: center; justify-content: center; }
          .ab-acc__check-item strong { color: #fff; font-weight: 800; }
          .ab-acc__empty { color: rgba(255,255,255,.9); font-size: 14px; }

          @media (max-width: 900px) {
            .ab-parallax { background-attachment: scroll; padding: 70px 0 160px; }
            .ab-parallax__card { max-width: 100%; padding: 28px; }

            .ab-accordion { margin-top: -120px; }
            .ab-accordion__panel { grid-template-columns: 1fr; }
            .ab-accordion__media { aspect-ratio: 16/9; min-height: 0; }
          }
          @media (max-width: 600px) {
            .ab-parallax { padding: 50px 0 110px; }
            .ab-parallax__inner { padding: 0 24px; }
            .ab-parallax__row { flex-direction: column; align-items: flex-start; }
            .ab-parallax__btn { width: 100%; justify-content: center; }

            .ab-accordion { margin-top: -70px; padding-bottom: 48px; }
            .ab-accordion__inner { padding: 0 24px; }
            .ab-accordion__tabs { flex-wrap: wrap; }
            .ab-accordion__tab { flex: 1 1 50%; padding: 16px 10px; font-size: 11px; }
            .ab-accordion__content { padding: 28px 24px; }
          }

          /* ── FAQ SECTION ── */
          .ab-faq { background: #f5f7fa; padding: 64px 0 88px; }
          .ab-faq__inner {
            max-width: 1280px; margin: 0 auto; padding: 0 40px;
            display: grid; grid-template-columns: 0.85fr 1.15fr;
            gap: 56px; align-items: start;
          }

          .ab-faq__intro { display: flex; flex-direction: column; gap: 18px; }
          .ab-faq__heading { font-size: clamp(26px, 3vw, 38px); font-weight: 900; color: #1a1a2e; line-height: 1.15; }
          .ab-faq__desc { font-size: 14.5px; color: #5a5f72; line-height: 1.8; }

          .ab-faq__cta {
            margin-top: 12px; position: relative; overflow: hidden;
            background: linear-gradient(135deg, #dfe6f5 0%, #cdd8ef 100%);
            border-radius: 6px; padding: 28px 28px 32px;
          }
          .ab-faq__cta-heading { font-size: 20px; font-weight: 800; color: #1a1a2e; margin-bottom: 10px; }
          .ab-faq__cta-desc { font-size: 13.5px; color: #50556b; line-height: 1.7; margin-bottom: 18px; max-width: 360px; }
          .ab-faq__cta-btn {
            display: inline-flex; align-items: center; gap: 8px;
            background: #029add; color: #fff;
            font-size: 13px; font-weight: 800; letter-spacing: .04em;
            padding: 12px 20px; border-radius: 3px;
            transition: background .2s, transform .2s;
          }
          .ab-faq__cta-btn:hover { background: #029add; transform: translateY(-2px); }

          .ab-faq__list { display: flex; flex-direction: column; gap: 12px; }
          .ab-faq__item { background: #fff; border: 1px solid #e8eaf0; border-radius: 6px; overflow: hidden; transition: border-color .2s; }
          .ab-faq__item--open { border-color: #c9d6f0; }
          .ab-faq__q {
            width: 100%; display: flex; align-items: center; justify-content: space-between;
            gap: 16px; padding: 18px 22px; background: #fff; border: none;
            cursor: pointer; text-align: left; font-size: 15px; font-weight: 700;
            color: #1a1a2e; transition: background .2s, color .2s;
          }
          .ab-faq__item--open .ab-faq__q { background: #eef2fb; color: #013d97; }
          .ab-faq__icon {
            flex-shrink: 0; width: 22px; height: 22px; display: flex;
            align-items: center; justify-content: center;
            font-size: 18px; font-weight: 600; color: inherit; line-height: 1;
          }
          .ab-faq__a {
            display: flex; align-items: flex-start; gap: 18px;
            padding: 0 22px 20px; background: #fff;
          }
          .ab-faq__a-img {
            flex-shrink: 0; width: 96px; height: 96px;
            object-fit: cover; border-radius: 6px;
          }
          .ab-faq__a-text { flex: 1; font-size: 13.5px; color: #5a5f72; line-height: 1.75; }

          @media (max-width: 900px) {
            .ab-body__inner { grid-template-columns: 1fr; gap: 56px; padding: 0 24px; }
            .ab-img-stack { max-width: 480px; margin: 0 auto; }
            .ab-faq__inner { grid-template-columns: 1fr; gap: 40px; padding: 0 24px; }
          }

          @media (max-width: 600px) {
            .ab-hero { height: 200px; }
            .ab-hero__content { padding: 20px 20px; }
            .ab-hero__title { font-size: 28px; }
            .ab-body { padding: 48px 0 60px; }
            .ab-cards { grid-template-columns: 1fr; }
            .ab-cta { flex-direction: column; align-items: flex-start; }
            .ab-faq { padding: 48px 0 60px; }
            .ab-faq__a { flex-direction: column; }
            .ab-faq__a-img { width: 100%; height: 140px; }
          }

          a:focus-visible, button:focus-visible { outline: 2px solid #029add; outline-offset: 2px; }
          @media (prefers-reduced-motion: reduce) { .ab *, .ab *::before, .ab *::after { transition-duration: 0ms !important; } }
        `}</style>
      </div>
      <Footer />
    </>
  );
}

export default AboutPage;